import Head from 'next/head';
import { type FC, type ReactElement, useEffect } from 'react';
import { RENDER_ENV_COOKIE } from 'src/lib/render-env-cookie';

const STUB_SRC = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
const SETTINGS_ORIGIN = 'https://cdn.cookielaw.org/consent/';
const DOMAIN_SCRIPT_ID = '33c336c7-85e9-4246-9792-798feeeb4ed8';

/**
 * Prebuilt pages are rendered with production values on every deployment, so
 * the deployment check runs in the browser, reading the environment the same
 * way `src/env.js` does: the middleware cookie first, then the values
 * `_document` embeds in the page. The site's settings file is preloaded so
 * OneTrust doesn't wait for the stub to request it; its URL has no version.
 */
const loadStubScript = `(function () {
  if (window.__plaidOneTrustLoader) {
    return;
  }
  window.__plaidOneTrustLoader = true;
  var env = window.__RENDER_ENV__ || {};
  var prefix = ${JSON.stringify(`${RENDER_ENV_COOKIE}=`)};
  var cookie = document.cookie.split('; ').find(function (entry) {
    return entry.indexOf(prefix) === 0;
  });
  if (cookie) {
    try {
      env = JSON.parse(decodeURIComponent(cookie.slice(prefix.length)));
    } catch (error) {}
  }
  if (env.NEXT_PUBLIC_IS_PREVIEW) {
    return;
  }
  var domainScript =
    env.PLAID_ENV === 'production'
      ? ${JSON.stringify(DOMAIN_SCRIPT_ID)}
      : ${JSON.stringify(`${DOMAIN_SCRIPT_ID}-test`)};
  var settings = document.createElement('link');
  settings.rel = 'preload';
  settings.setAttribute('as', 'fetch');
  settings.setAttribute('crossorigin', 'anonymous');
  settings.href =
    ${JSON.stringify(
      SETTINGS_ORIGIN,
    )} + domainScript + '/' + domainScript + '.json';
  document.head.appendChild(settings);
  var stub = document.createElement('script');
  stub.async = true;
  stub.src = ${JSON.stringify(STUB_SRC)};
  stub.setAttribute('data-domain-script', domainScript);
  document.head.appendChild(stub);
})();`;

const MARKER_NAME = 'plaid-consent-manager';

/**
 * Marks the page so `_document` loads OneTrust in `<head>`. Pages reached by a
 * client-side navigation have no server-rendered loader, so it runs here once.
 */
export const OneTrustPageMarker: FC = () => {
  useEffect(() => {
    if (!window.__plaidOneTrustLoader) {
      const loader = document.createElement('script');
      loader.text = loadStubScript;
      document.head.appendChild(loader);
    }
  }, []);

  return (
    <Head>
      <meta key={MARKER_NAME} name={MARKER_NAME} content='onetrust' />
    </Head>
  );
};

export const hasOneTrustPageMarker = (
  head: ReadonlyArray<ReactElement | null> | undefined,
): boolean => {
  return (head ?? []).some((element) => {
    return element?.type === 'meta' && element.props?.name === MARKER_NAME;
  });
};

/**
 * OneTrust's stub in `<head>`, as OneTrust recommends, so consent (and
 * everything gated on it) resolves without waiting for the app bundle. It
 * loads the banner version published in the OneTrust dashboard. `_document`
 * renders it after the `window.__RENDER_ENV__` script, which the loader reads.
 */
export const OneTrustHeadTags: FC = () => {
  return (
    <>
      <link rel='preconnect' href='https://cdn.cookielaw.org' />
      <link
        rel='preconnect'
        href='https://geolocation.onetrust.com'
        crossOrigin='anonymous'
      />
      <script dangerouslySetInnerHTML={{ __html: loadStubScript }} />
    </>
  );
};
